// Manage the Comms Book's category list -- add, rename, recolour,
// reorder, and deactivate. Categories are never hard-deleted (matches the
// is_active soft-delete convention already used for roster groups/shift
// types) since messages reference them by id.
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { ArrowUp, ArrowDown, Plus } from "lucide-react";
import { loadAllCommsCategories, createCommsCategory, updateCommsCategory, type CommsCategory } from "@/lib/commsBook";

export default function CommsCategoryManager({
  open,
  onOpenChange,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onChanged: () => void;
}) {
  const [categories, setCategories] = useState<CommsCategory[]>([]);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#eab308");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setCategories(await loadAllCommsCategories());
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const notifyChanged = () => {
    load();
    onChanged();
  };

  const handleRename = async (id: string, name: string) => {
    if (!name.trim()) return;
    await updateCommsCategory(id, { name: name.trim() });
    notifyChanged();
  };

  const handleColorChange = async (id: string, color: string) => {
    await updateCommsCategory(id, { color_hex: color });
    notifyChanged();
  };

  const handleToggleActive = async (cat: CommsCategory) => {
    await updateCommsCategory(cat.id, { is_active: !cat.is_active });
    notifyChanged();
  };

  const handleMove = async (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= categories.length) return;
    const a = categories[index];
    const b = categories[targetIndex];
    await Promise.all([updateCommsCategory(a.id, { sort_order: b.sort_order }), updateCommsCategory(b.id, { sort_order: a.sort_order })]);
    notifyChanged();
  };

  const handleAdd = async () => {
    if (!newName.trim()) {
      toast({ title: "Category needs a name", variant: "destructive" });
      return;
    }
    setSaving(true);
    const maxSort = categories.reduce((max, c) => Math.max(max, c.sort_order), 0);
    const ok = await createCommsCategory(newName.trim(), newColor, maxSort + 1);
    setSaving(false);
    if (!ok) {
      toast({ title: "Failed to add category", variant: "destructive" });
      return;
    }
    setNewName("");
    notifyChanged();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Manage Comms Book Categories</DialogTitle>
        </DialogHeader>

        <div className="space-y-2">
          {categories.map((cat, i) => (
            <div key={cat.id} className={`flex items-center gap-2 rounded-md border border-border p-2 ${!cat.is_active ? "opacity-50" : ""}`}>
              <input
                type="color"
                value={cat.color_hex || "#888888"}
                onChange={(e) => handleColorChange(cat.id, e.target.value)}
                className="h-7 w-7 rounded border border-border cursor-pointer shrink-0"
                title="Category colour"
              />
              <Input
                defaultValue={cat.name}
                onBlur={(e) => e.target.value !== cat.name && handleRename(cat.id, e.target.value)}
                className="h-8 flex-1"
              />
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleMove(i, -1)} disabled={i === 0}>
                  <ArrowUp className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleMove(i, 1)} disabled={i === categories.length - 1}>
                  <ArrowDown className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 pl-1">
                <Checkbox id={`cat-active-${cat.id}`} checked={cat.is_active} onCheckedChange={() => handleToggleActive(cat)} />
                <Label htmlFor={`cat-active-${cat.id}`} className="text-xs text-muted-foreground cursor-pointer">
                  Active
                </Label>
              </div>
            </div>
          ))}
          {categories.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No categories yet.</p>}
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-border">
          <input
            type="color"
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
            className="h-9 w-9 rounded border border-border cursor-pointer shrink-0"
          />
          <Input placeholder="New category name" value={newName} onChange={(e) => setNewName(e.target.value)} className="flex-1" />
          <Button onClick={handleAdd} disabled={saving}>
            <Plus className="w-4 h-4 mr-2" />
            Add
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
